import { state } from "../state";
import * as api from "./apps.api";
import { CognniApp } from "@/common/models/app.modal";
import { EditionStr, isEditionHighEnough } from "@/common/models";
import { isNotEmpty } from "@/fp";
import { computed } from "@vue/composition-api";

const { getter, m, action, mutation } = state;

const raw_settings = getter((s) => s.raw_settings);
const set_raw_settings = m.set("raw_settings");

const appFlags = getter((s) => s.orgAppFlags);
const setAppFlags = m.set("orgAppFlags");

const updateAppFlag = action<[string, boolean]>(
  "update app flag",
  async ({ mutate, state }, [flagName, isOn]) => {
    await api.toggleAppFlag({ flagName, isOn });
    mutate(setAppFlags, {
      ...state.orgAppFlags,
      [flagName]: isOn,
    });
  }
);

const rollout = getter((s) => s.rollout);
const setRollout = m.set("rollout");

const onPrem = getter((s) => s.onPrem);
const setOnPrem = m.set("onPrem");

const apps = getter((s) =>
  s.apps
    .filter((app) => app.group !== "solution")
    .map((app) => {
      app.allow =
        isEditionHighEnough({
          minEdition: app.edition,
          userEdition: s.user.paymentEdition.name,
        }) || app.enabledByFlag;
      if (app.addons) {
        app.addons = app.addons.map((addon) => {
          return {
            ...addon,
            allow:
              isEditionHighEnough({
                minEdition: addon.edition,
                userEdition: s.user.paymentEdition.name,
              }) || app.enabledByFlag,
          };
        });
      }
      return app;
    })
    .sort((a, b) => a.order - b.order)
);

const setApps = m.set("apps");

const loadApps = action<void>("load apps", async ({ mutate, state }) => {
  const res = await api.getAllApps(
    state.user.paymentEdition.name as EditionStr
  );

  if (res.success) {
    const { apps, flags, rollout, onPrem, raw } = res.value;
    console.log("raw : ", raw);
    const DEFAULT_RAW_SETTINGS = {
      information_risk: {
        activation_date: new Date().toISOString(),
      },
    };
    mutate(set_raw_settings, { ...DEFAULT_RAW_SETTINGS, ...raw });
    mutate(
      setApps,
      apps.map((app: CognniApp) => {
        if (app.addons) {
          app.addons = app.addons.map((addon) => {
            return {
              ...addon,
              hasLearnMore: isNotEmpty(addon.description),
            };
          });
        }
        return {
          ...app,
          hasLearnMore: isNotEmpty(app.description),
        };
      })
    );
    mutate(setAppFlags, flags);
    mutate(setRollout, rollout);
    mutate(setOnPrem, onPrem);
  }
});

const updateAppSetupKeys = action<{
  keys: { [key: string]: string };
  service: string;
}>("update app setup keys", async ({ state, mutate }, { service, keys }) => {
  const res = await api.connectToService({
    service,
    keys,
  });
  if (res.success) {
    mutate(
      setApps,
      state.apps.map((app) => {
        if (app.id === service) {
          // @ts-ignore
          app.setup.keys = app.setup.keys.map((s) => {
            return {
              ...s,
              value: keys[s.key] || s.value,
            };
          });
        }
        return app;
      })
    );
  }
});

const generateAppKey = action<{ service: string; key: string }>(
  "update app setup keys",
  async ({ state, mutate }, { key, service }) => {
    const res = await api.generateKey(service);
    if (res.success) {
      mutate(
        setApps,
        state.apps.map((app) => {
          if (app.id === service) {
            // @ts-ignore
            app.setup.keys = app.setup.keys.map((s) => {
              return {
                ...s,
                value: s.key === key ? res.value : s.value,
              };
            });
          }
          return app;
        })
      );
    }
  }
);

const addAppAddon = action<{ parentAppId: string; addon: CognniApp }>(
  "add app addon",
  async ({ mutate, state }, { addon, parentAppId }) => {
    const settings = {
      added: true,
      // if an addon can be toggled then it will start with an OFF state
      // otherwise an ON.
      on: true, //!addon.canBe.toggled,
      pined: false,
    };
    const res = await api.updateOrgAppsSettings({
      key: addon.id,
      settings,
    });
    if (res.success) {
      // mutate(updateAppSettings, { settings, key: addon.id });
      mutate(
        setApps,
        state.apps.map((_app) => {
          if (_app.id === parentAppId) {
            return {
              ..._app,
              is: {
                added: true,
                on: true,
                pined: false,
              },
              addons: (_app.addons || []).map((a) => {
                if (a.id === addon.id) {
                  return {
                    ...a,
                    is: settings,
                  };
                } else {
                  return a;
                }
              }),
            };
          }
          return _app;
        })
      );
    }
  }
);

const addApp = action<CognniApp>("add app", async ({ mutate, state }, app) => {
  const activation_date = new Date().toISOString();

  const settings = {
    added: true,
    on: true,
    pined: false,
    activation_date,
  };

  const res = await api.updateOrgAppsSettings({
    key: app.id,
    settings,
  });

  if (res.success) {
    mutate(
      setApps,
      state.apps.map((_app) => {
        if (_app.id === app.id) {
          return {
            ..._app,
            activation_date,
            is: {
              added: true,
              on: true,
              pined: false,
            },
          };
        }
        return _app;
      })
    );
  }
});

const removeApp = action<CognniApp>(
  "pin app",
  async ({ mutate, state }, app) => {
    const settings = {
      added: false,
      on: false,
      pined: false,
    };

    const res = await api.updateOrgAppsSettings({
      key: app.id,
      settings,
    });
    if (res.success) {
      //    mutate(updateAppSettings, { settings, key: app.id });
      mutate(
        setApps,
        state.apps.map((_app) => {
          if (_app.id === app.id) {
            return {
              ..._app,
              is: {
                added: false,
                on: false,
                pined: false,
              },
            };
          }
          return _app;
        })
      );
    }
  }
);

const removeAppAddon = action<{ parentAppId: string; addon: CognniApp }>(
  "pin app",
  async ({ mutate, state }, { parentAppId, addon }) => {
    const settings = {
      added: false,
      on: false,
      pined: false,
    };

    const res = await api.updateOrgAppsSettings({
      key: addon.id,
      settings,
    });
    if (res.success) {
      //     mutate(updateAppSettings, { settings, key: addon.id });
      mutate(
        setApps,
        state.apps.map((_app) => {
          if (_app.id === parentAppId) {
            return {
              ..._app,
              is: {
                added: false,
                on: false,
                pined: false,
              },
              addons: (_app.addons || []).map((a) => {
                if (a.id === addon.id) {
                  return {
                    ...a,
                    is: settings,
                  };
                }
                return a;
              }),
            };
          }
          return _app;
        })
      );
    }
  }
);

const togglePin = action<CognniApp>(
  "pin app",
  async ({ mutate, state }, app) => {
    const settings = {
      added: true,
      on: true,
      pined: !app.is.pined,
    };

    const res = await api.updateOrgAppsSettings({ settings, key: app.id });
    if (res.success) {
      //    mutate(updateAppSettings, { settings, key: app.id });
      mutate(
        setApps,
        state.apps.map((_app) => {
          if (_app.id === app.id) {
            return {
              ..._app,
              is: {
                ..._app.is,
                pined: !_app.is.pined,
              },
            };
          }
          return _app;
        })
      );
    }
  }
);

const toggleOn = action<CognniApp>(
  "pin app",
  async ({ mutate, state }, app) => {
    const settings = {
      added: true,
      pined: app.is.on ? false : app.is.pined,
      on: !app.is.on,
    };
    const res = await api.updateOrgAppsSettings({ settings, key: app.id });
    if (res.success) {
      //     mutate(updateAppSettings, { settings, key: app.id });
      mutate(
        setApps,
        state.apps.map((_app) => {
          if (_app.id === app.id) {
            return {
              ..._app,
              is: {
                ..._app.is,
                pined: _app.is.on ? false : _app.is.pined,
                on: !_app.is.on,
              },
            };
          }
          return _app;
        })
      );
    }
  }
);

const getIsAppOn = (appId: string, parentId?: string): boolean => {
  if (parentId) {
    const app = apps.value.find((a) => a.id === parentId);
    if (app && app.addons) {
      const addOn = app.addons.find((a) => a.id === appId) as CognniApp;
      return app.is.on && addOn && addOn.is.on;
    } else {
      return false;
    }
  } else {
    const app = apps.value.find((a) => a.id === appId) as CognniApp;
    return app && app.is.on;
  }
};

const solutions = getter((s) =>
  s.apps.filter(
    (t) =>
      t.group === "solution" &&
      (t.is.added || s.orgAppFlags[`flag_app_enable_${t.id}`])
  )
);
const has_solutions = computed(() => solutions.value.length > 0);

const is_mip_enabled = computed(() => {
  return isNotEmpty(solutions.value.find((l) => l.id === "mip"));
});

const is_on_prem_enabled = computed(() => {
  return isNotEmpty(solutions.value.find((l) => l.id === "iie"));
});

//@ts-ignore
window.solutions = () => solutions.value;

export {
  raw_settings,
  solutions,
  is_mip_enabled,
  is_on_prem_enabled,
  has_solutions,
  getIsAppOn,
  appFlags,
  rollout,
  onPrem,
  updateAppFlag,
  apps,
  loadApps,
  togglePin,
  toggleOn,
  addApp,
  addAppAddon,
  removeApp,
  removeAppAddon,
  updateAppSetupKeys,
  generateAppKey,
};
